import { useContext, useEffect, useRef } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";
import type { ExitRoute } from "./useConfigEditor";

/**
 * SIMS-9123 — holds an in-app navigation away from a dirty editor and asks first.
 *
 * Cancel and the breadcrumb already go through the editor's discard prompt because the editor
 * owns them. A link elsewhere in the app (the left navigation, the user menu, back/forward) is
 * not the editor's, and before this it discarded the draft in silence. `useBlocker` stops that
 * transition; the blocked navigation is then handed to `requestExit` as route "navigate", so the
 * user sees the SAME prompt: Discard proceeds with the navigation, Keep editing cancels it.
 *
 * Only a change of PATHNAME is held. The editor renders in place of the list on the same path, so
 * a search-param or hash change is not leaving it.
 */
interface NavigationBlockerProps {
	/** Hold navigations only while the editor has unsaved changes. */
	when: boolean;
	/** Whether the editor's discard prompt is showing. */
	promptOpen: boolean;
	requestExit: (proceed: () => void, route?: ExitRoute) => void;
}

const Blocker = ({
	when,
	promptOpen,
	requestExit,
}: NavigationBlockerProps): null => {
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			when && currentLocation.pathname !== nextLocation.pathname,
	);
	// The prompt opens one render after `requestExit`, so "closed" alone cannot mean "kept
	// editing": both refs together say the user saw the prompt for THIS navigation and left it.
	const askedRef = useRef(false);
	const promptSeenRef = useRef(false);

	useEffect(() => {
		// The refs are cleared only once the router itself is unblocked again. Clearing them in
		// the proceed callback let the very next render — still "blocked" while the navigation
		// starts — ask a second time, and with the editor clean by then `requestExit` proceeded
		// at once: a second `proceed()` on a blocker that was already unblocked.
		if (blocker.state === "unblocked") {
			askedRef.current = false;
			promptSeenRef.current = false;
			return;
		}
		if (blocker.state !== "blocked" || askedRef.current) return;
		askedRef.current = true;
		promptSeenRef.current = false;
		requestExit(() => blocker.proceed?.(), "navigate");
	}, [blocker, requestExit]);

	useEffect(() => {
		if (!askedRef.current) return;
		if (promptOpen) {
			promptSeenRef.current = true;
			return;
		}
		if (promptSeenRef.current && blocker.state === "blocked") {
			// Keep editing: stay here and drop the held navigation. `askedRef` clears on the
			// "unblocked" render that follows, as above.
			promptSeenRef.current = false;
			blocker.reset?.();
		}
	}, [promptOpen, blocker]);

	return null;
};

/**
 * Renders the guard only under a data router. `useBlocker` throws anywhere else, and screens and
 * tests that mount an editor under a plain router (or none) keep working, without the guard.
 */
const NavigationBlocker = (
	props: NavigationBlockerProps,
): JSX.Element | null => {
	const dataRouter = useContext(UNSAFE_DataRouterContext);
	return dataRouter ? <Blocker {...props} /> : null;
};

export default NavigationBlocker;
